/**
 * Media Generation Canvas Component
 *
 * Slide-over panel for AI media generation workflow:
 * - Shows real-time generation progress via SSE
 * - Displays generated media with selection options
 * - Supports regeneration (with limit tracking)
 * - "Continue" to proceed to campaign generation (Phase 2)
 *
 * Part of two-phase media generation workflow:
 * Phase 1: Generate and select media
 * Phase 2: Generate campaign with selected media
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SidePanel } from '@/components/ui/SidePanel';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { apiClient } from '@/services/api';
import { useSubscription } from '@/hooks/useSubscription';
import { useCreditCost } from '@/hooks/usePricingCatalog';
import {
  Sparkles,
  Check,
  RefreshCw,
  X,
  ArrowRight,
  Image as ImageIcon,
  Video,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
  Coins,
  Upload,
} from 'lucide-react';

// Types
interface GeneratedMedia {
  url: string;
  asset_id?: string;
  media_type: 'image' | 'video';
  job_id: string;
  filename: string;
  selected: boolean;
  generation_mode?: 'text_to_image' | 'image_to_image' | 'text_to_video' | 'image_to_video';
}

interface MediaJob {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  media_type: 'image' | 'video';
  generation_mode?: 'text_to_image' | 'image_to_image' | 'text_to_video' | 'image_to_video';
  count: number;
  output_urls: string[];
  output_asset_ids: string[];
  error?: string;
  progress?: number;
}

interface MediaWorkflowState {
  state: 'none' | 'media_generation_pending' | 'media_selection_pending' | 'media_selected' | 'completed';
  session_id?: string;
  job_ids: string[];
  generated_media: GeneratedMedia[];
  selected_media: GeneratedMedia[];
  all_jobs_complete: boolean;
}

interface MediaGenerationCapabilities {
  image_generation: {
    enabled: boolean;
    limit: number;
    used: number;
    remaining: number;
  };
  video_generation: {
    enabled: boolean;
    limit: number;
    used: number;
    remaining: number;
  };
  regeneration_attempts_per_campaign: number;
  max_media_per_campaign: number;
}

interface OriginalMedia {
  id: string;
  url: string;
  filename: string;
  media_type: 'image' | 'video';
}

interface MediaGenerationCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  onContinue: (selectedMedia: GeneratedMedia[]) => void;
  onError?: (error: string) => void;
  originalMedia?: OriginalMedia[];
}

export const MediaGenerationCanvas: React.FC<MediaGenerationCanvasProps> = ({
  isOpen,
  onClose,
  conversationId,
  onContinue,
  onError,
  originalMedia = [],
}) => {
  // Hooks
  const { subscription, reload: reloadSubscription } = useSubscription();

  // State
  const [workflowState, setWorkflowState] = useState<MediaWorkflowState | null>(null);
  const [jobs, setJobs] = useState<Map<string, MediaJob>>(new Map());
  const [generatedMedia, setGeneratedMedia] = useState<GeneratedMedia[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [capabilities, setCapabilities] = useState<MediaGenerationCapabilities | null>(null);
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Refs for SSE connections and polling timers
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map());
  const pollingTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pollingActiveRef = useRef<Set<string>>(new Set()); // Track which jobs are polling
  const sseAttemptedRef = useRef<Set<string>>(new Set()); // Track SSE connection attempts

  // Credit cost calculation helpers
  const imageCost = useCreditCost('image_generation') ?? 0;
  const videoCost = useCreditCost('video_generation') ?? 0;
  const calculateMediaCreditCost = useCallback((media: GeneratedMedia[]): number => {
    return media.reduce((total, m) => total + (m.media_type === 'video' ? videoCost : imageCost), 0);
  }, [imageCost, videoCost]);

  const getRegenerationCreditCost = useCallback((): number => {
    // Calculate cost to regenerate all currently generated media
    return calculateMediaCreditCost(generatedMedia);
  }, [generatedMedia, calculateMediaCreditCost]);

  const hasInsufficientCredits = useCallback((requiredCredits: number): boolean => {
    if (!subscription?.credits) return false;
    // Unlimited credits (-1) means always sufficient
    if (subscription.credits.available_credits === -1 || subscription.credits.total_credits === -1) return false;
    return subscription.credits.available_credits < requiredCredits;
  }, [subscription]);

  // Load workflow state and capabilities
  useEffect(() => {
    if (isOpen && conversationId) {
      loadWorkflowState();
      loadCapabilities();
    }

    return () => {
      // Cleanup SSE connections on unmount
      eventSourcesRef.current.forEach((es) => es.close());
      eventSourcesRef.current.clear();

      // Cleanup polling timers on unmount
      pollingTimersRef.current.forEach((timer) => clearTimeout(timer));
      pollingTimersRef.current.clear();

      // Clear tracking sets
      pollingActiveRef.current.clear();
      sseAttemptedRef.current.clear();
    };
  }, [isOpen, conversationId]);

  // Pre-populate jobs Map from workflow state job_ids
  // This ensures progress UI shows immediately while waiting for SSE/polling
  useEffect(() => {
    if (!workflowState?.job_ids?.length) return;

    // Initialize jobs Map with pending status for jobs we don't know about yet
    setJobs((prevJobs) => {
      const newJobs = new Map(prevJobs);
      let hasChanges = false;

      workflowState.job_ids.forEach((jobId) => {
        if (!newJobs.has(jobId)) {
          // Initialize with pending status - will be updated by SSE/polling
          newJobs.set(jobId, {
            job_id: jobId,
            status: 'pending',
            media_type: 'image', // Default, will be updated
            count: 4, // Default, will be updated
            output_urls: [],
            output_asset_ids: [],
          });
          hasChanges = true;
        }
      });

      return hasChanges ? newJobs : prevJobs;
    });
  }, [workflowState?.job_ids]);

  // Subscribe to SSE for all pending/processing jobs
  // Eagerly establish SSE connections when job_ids appear
  useEffect(() => {
    if (!workflowState?.job_ids?.length) return;

    workflowState.job_ids.forEach((jobId) => {
      // Skip if we've already attempted SSE for this job
      // This prevents infinite reconnection loops when SSE fails
      if (sseAttemptedRef.current.has(jobId)) {
        return;
      }

      // Skip if already connected via SSE
      if (eventSourcesRef.current.has(jobId)) {
        return;
      }

      const job = jobs.get(jobId);
      // Only subscribe if job is not complete and not already polling
      if (!job || !['completed', 'failed', 'cancelled'].includes(job.status)) {
        // If already polling, don't try SSE
        if (pollingActiveRef.current.has(jobId)) {
          return;
        }
        subscribeToJobStatus(jobId);
      }
    });
  }, [workflowState?.job_ids, jobs]);

  const loadWorkflowState = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/v1/campaigns/${conversationId}/media-workflow`
      );
      setWorkflowState(response.data);

      // Initialize generated media from workflow state
      if (response.data.generated_media?.length > 0) {
        const media: GeneratedMedia[] = response.data.generated_media.map((m: any) => ({
          ...m,
          selected: response.data.selected_media?.some(
            (s: any) => s.asset_id === m.asset_id
          ) || false,
        }));
        setGeneratedMedia(media);

        // Initialize selected IDs
        const selected = new Set<string>(
          response.data.selected_media?.map((m: any) => m.asset_id) || []
        );
        setSelectedIds(selected);
      }
    } catch (err: any) {
      console.error('Failed to load workflow state:', err);
      setError('Failed to load media generation status');
    } finally {
      setLoading(false);
    }
  };

  const loadCapabilities = async () => {
    try {
      const response = await apiClient.get('/api/v1/media/capabilities');
      setCapabilities(response.data);
    } catch (err) {
      console.error('Failed to load capabilities:', err);
    }
  };

  const subscribeToJobStatus = (jobId: string) => {
    // Mark that we've attempted SSE for this job
    // This prevents infinite reconnection loops
    sseAttemptedRef.current.add(jobId);

    const baseUrl = apiClient.defaults.baseURL || '';
    // Get auth token directly from storage (not from apiClient headers, as those are set per-request via interceptor)
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
    // SSE cannot send custom headers, so pass token as query parameter
    const url = token
      ? `${baseUrl}/api/v1/media/status/${jobId}/stream?token=${encodeURIComponent(token)}`
      : `${baseUrl}/api/v1/media/status/${jobId}/stream`;

    const eventSource = new EventSource(url, {
      withCredentials: true,
    });

    eventSource.addEventListener('status', (event) => {
      const data = JSON.parse(event.data);
      updateJobStatus(jobId, data);
    });

    eventSource.addEventListener('complete', (event) => {
      const data = JSON.parse(event.data);
      updateJobStatus(jobId, data);
      handleJobComplete(jobId, data);
      eventSource.close();
      eventSourcesRef.current.delete(jobId);
    });

    eventSource.addEventListener('error', (event) => {
      console.error(`SSE error for job ${jobId}:`, event);
      eventSource.close();
      eventSourcesRef.current.delete(jobId);

      // Only fallback to polling if not already polling
      // This prevents duplicate polling when SSE continuously fails
      if (!pollingActiveRef.current.has(jobId)) {
        console.log(`Starting fallback polling for job ${jobId}`);
        pollJobStatus(jobId);
      } else {
        console.log(`Job ${jobId} already polling, skipping duplicate start`);
      }
    });

    eventSourcesRef.current.set(jobId, eventSource);
  };

  const pollJobStatus = async (jobId: string, retryCount: number = 0) => {
    const MAX_RETRIES = 5;
    const POLL_INTERVAL = 2000;

    // Mark polling as active for this job
    pollingActiveRef.current.add(jobId);

    // Clear any existing timer for this job
    const existingTimer = pollingTimersRef.current.get(jobId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      pollingTimersRef.current.delete(jobId);
    }

    try {
      const response = await apiClient.get(`/api/v1/media/status/${jobId}`);
      updateJobStatus(jobId, response.data);

      if (['completed', 'failed', 'cancelled'].includes(response.data.status)) {
        handleJobComplete(jobId, response.data);
        pollingTimersRef.current.delete(jobId);
        pollingActiveRef.current.delete(jobId); // Stop polling
      } else {
        // Continue polling - reset retry count on success
        const timer = setTimeout(() => pollJobStatus(jobId, 0), POLL_INTERVAL);
        pollingTimersRef.current.set(jobId, timer);
      }
    } catch (err) {
      console.error(`Failed to poll job status (attempt ${retryCount + 1}/${MAX_RETRIES}):`, err);

      // Stop polling after max retries to prevent resource exhaustion
      if (retryCount >= MAX_RETRIES) {
        console.error(`Max polling retries reached for job ${jobId}. Stopping.`);
        pollingTimersRef.current.delete(jobId);
        pollingActiveRef.current.delete(jobId); // Stop polling
        // Mark job as failed in UI
        updateJobStatus(jobId, {
          job_id: jobId,
          status: 'failed',
          error: 'Failed to fetch job status after multiple retries',
          media_type: 'image',
          count: 0,
          output_urls: [],
          output_asset_ids: [],
        });
        return;
      }

      // Retry with exponential backoff
      const backoff = POLL_INTERVAL * Math.pow(2, retryCount);
      const timer = setTimeout(() => pollJobStatus(jobId, retryCount + 1), backoff);
      pollingTimersRef.current.set(jobId, timer);
    }
  };

  const updateJobStatus = (jobId: string, data: MediaJob) => {
    setJobs((prev) => {
      const newJobs = new Map(prev);
      newJobs.set(jobId, data);
      return newJobs;
    });
  };

  const handleJobComplete = useCallback((jobId: string, data: MediaJob) => {
    if (data.status === 'completed' && data.output_urls?.length > 0) {
      // Add generated media to list
      const newMedia: GeneratedMedia[] = data.output_urls.map((url, i) => ({
        url,
        asset_id: data.output_asset_ids?.[i],
        media_type: data.media_type,
        job_id: jobId,
        filename: `generated_${i + 1}.${data.media_type === 'video' ? 'mp4' : 'jpg'}`,
        selected: false,
        generation_mode: data.generation_mode,
      }));

      setGeneratedMedia((prev) => {
        // Deduplicate: filter out existing media with same asset_ids
        const existingAssetIds = new Set(prev.map(m => m.asset_id).filter(Boolean));
        const uniqueNewMedia = newMedia.filter(m => !existingAssetIds.has(m.asset_id));
        return [...prev, ...uniqueNewMedia];
      });

      // Reload subscription to update credit balance
      reloadSubscription();
      // Notify other subscription instances (e.g. header credit display) to refresh
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    }

    // Check if all jobs are complete
    checkAllJobsComplete();
  }, [reloadSubscription]);

  const checkAllJobsComplete = () => {
    // Reload workflow state to get updated completion status
    loadWorkflowState();
  };

  const toggleMediaSelection = (assetId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        // Check max selection limit (-1 means unlimited)
        const maxLimit = capabilities?.max_media_per_campaign || 10;
        if (capabilities && maxLimit !== -1 && newSet.size >= maxLimit) {
          setError(`Maximum ${maxLimit} media items allowed`);
          return prev;
        }
        newSet.add(assetId);
      }
      return newSet;
    });

    // Only update generatedMedia's selected state for AI-generated items
    const isGeneratedItem = generatedMedia.some(m => m.asset_id === assetId);
    if (isGeneratedItem) {
      setGeneratedMedia((prev) =>
        prev.map((m) =>
          m.asset_id === assetId ? { ...m, selected: !m.selected } : m
        )
      );
    }
  };

  const selectAll = () => {
    const maxLimit = capabilities?.max_media_per_campaign || 10;

    // Build combined pool: originals first, then generated
    const allOriginalIds = originalMedia.map(m => m.id);
    const allGeneratedIds = generatedMedia.map(m => m.asset_id).filter(Boolean) as string[];
    const combined = [...allOriginalIds, ...allGeneratedIds];

    const toSelect = maxLimit === -1 ? combined : combined.slice(0, maxLimit);
    const newSelectedIds = new Set<string>(toSelect);
    setSelectedIds(newSelectedIds);

    setGeneratedMedia((prev) =>
      prev.map((m) => ({ ...m, selected: m.asset_id ? newSelectedIds.has(m.asset_id) : false }))
    );
  };

  const selectNone = () => {
    setSelectedIds(new Set());
    setGeneratedMedia((prev) => prev.map((m) => ({ ...m, selected: false })));
  };

  const handleRegenerate = async () => {
    const regenLimit = capabilities?.regeneration_attempts_per_campaign;
    if (!capabilities || (regenLimit !== -1 && regenerationCount >= (regenLimit ?? 0))) {
      setError('Regeneration limit reached');
      return;
    }

    try {
      setIsRegenerating(true);
      setError(null);

      const response = await apiClient.post(
        `/api/v1/campaigns/${conversationId}/media-workflow/regenerate`
      );

      if (response.data.success) {
        setRegenerationCount((prev) => prev + 1);
        setGeneratedMedia([]);
        setSelectedIds(new Set());
        setJobs(new Map());

        // Subscribe to new jobs
        response.data.job_ids.forEach((jobId: string) => {
          subscribeToJobStatus(jobId);
        });

        // Reload workflow state
        loadWorkflowState();
      }
    } catch (err: any) {
      // Extract detailed error message from backend response
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to regenerate media';
      setError(errorMessage);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleContinue = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one media item');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Submit selection
      await apiClient.post(
        `/api/v1/campaigns/${conversationId}/media-workflow/select`,
        {
          selected_asset_ids: Array.from(selectedIds),
        }
      );

      // Collect selected AI-generated items
      const selectedGenerated = generatedMedia.filter(
        (m) => m.asset_id && selectedIds.has(m.asset_id)
      );

      // Collect selected original items, shaped to GeneratedMedia
      const selectedOriginals: GeneratedMedia[] = originalMedia
        .filter((m) => selectedIds.has(m.id))
        .map((m) => ({
          url: m.url,
          asset_id: m.id,
          media_type: m.media_type,
          job_id: 'original',
          filename: m.filename,
          selected: true,
        }));

      const selectedMedia = [...selectedOriginals, ...selectedGenerated];

      // Auto-close canvas before Phase 2 starts
      onClose();

      // Callback to continue with campaign generation (Phase 2)
      onContinue(selectedMedia);
    } catch (err: any) {
      setError(err.message || 'Failed to submit selection');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate overall progress
  const calculateProgress = (): number => {
    if (jobs.size === 0) return 0;

    let totalProgress = 0;
    jobs.forEach((job) => {
      switch (job.status) {
        case 'completed':
          totalProgress += 100;
          break;
        case 'processing':
          totalProgress += job.progress || 50;
          break;
        case 'pending':
          totalProgress += 10;
          break;
        case 'failed':
        case 'cancelled':
          totalProgress += 100; // Count as done
          break;
      }
    });

    return Math.round(totalProgress / jobs.size);
  };

  const allJobsComplete =
    workflowState?.all_jobs_complete ||
    (jobs.size > 0 &&
      Array.from(jobs.values()).every((j: MediaJob) =>
        ['completed', 'failed', 'cancelled'].includes(j.status)
      ));

  const hasFailedJobs = Array.from(jobs.values()).some(
    (j: MediaJob) => j.status === 'failed'
  );

  // -1 = no regeneration cap (every image is charged credits instead).
  const regenerationUnlimited = capabilities?.regeneration_attempts_per_campaign === -1;
  const regenerationsRemaining = capabilities
    ? (regenerationUnlimited ? Infinity : capabilities.regeneration_attempts_per_campaign - regenerationCount)
    : 0;

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="AI Media Generation"
      position="right"
      width="xl"
    >
      <SidePanel.Body>
        {loading && jobs.size === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-ink-2">
              Loading generation status...
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Progress Section - Show when workflow is active */}
            {/* Show initial loading state when workflow has job_ids but jobs Map is empty */}
            {!allJobsComplete && jobs.size === 0 && workflowState?.job_ids?.length > 0 && (
              <div className="bg-work-soft rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-work animate-pulse" />
                  <span className="font-medium text-work">
                    Initializing media generation...
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-work">
                    Connecting to {workflowState.job_ids.length} generation job{workflowState.job_ids.length > 1 ? 's' : ''}...
                  </span>
                </div>
              </div>
            )}

            {/* Progress Section - Show when jobs are loaded */}
            {!allJobsComplete && jobs.size > 0 && (
              <div className="bg-work-soft rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-work animate-pulse" />
                  <span className="font-medium text-work">
                    Generating media...
                  </span>
                </div>
                <ProgressBar
                  value={calculateProgress()}
                  color="primary"
                  size="md"
                />
                <div className="mt-2 text-sm text-work">
                  {Array.from(jobs.values()).filter((j: MediaJob) => j.status === 'completed')
                    .length}{' '}
                  of {jobs.size} jobs complete
                </div>
              </div>
            )}

            {/* Job Status List */}
            {jobs.size > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-ink-2">
                  Generation Jobs
                </h3>
                {Array.from(jobs.entries()).map(([jobId, job]) => (
                  <div
                    key={jobId}
                    className="flex items-center justify-between bg-ground dark:bg-card-darkAlt rounded-lg p-3"
                  >
                    <div className="flex items-center gap-3">
                      {job.media_type === 'video' ? (
                        <Video className="w-4 h-4 text-purple-500" />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-work" />
                      )}
                      <span className="text-sm">
                        {job.count} {job.media_type}
                        {job.count > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.status === 'pending' && (
                        <Badge variant="gray" size="sm">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                      {job.status === 'processing' && (
                        <Badge variant="primary" size="sm">
                          <Zap className="w-3 h-3 mr-1 animate-pulse" />
                          Processing
                        </Badge>
                      )}
                      {job.status === 'completed' && (
                        <Badge variant="success" size="sm">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Complete
                        </Badge>
                      )}
                      {job.status === 'failed' && (
                        <Badge variant="error" size="sm">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Original Images Section */}
            {originalMedia.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-ink-2">
                    Your Original Images
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {originalMedia.map((media) => (
                    <div
                      key={media.id}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedIds.has(media.id)
                          ? 'border-work-dot ring-2 ring-blue-200'
                          : 'border-line dark:border-border-dark hover:border-line-2'
                      }`}
                      onClick={() => toggleMediaSelection(media.id)}
                    >
                      {/* Media Preview */}
                      <div className="aspect-square bg-inset dark:bg-card-darkAlt">
                        {media.media_type === 'video' ? (
                          <video
                            src={media.url}
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt={media.filename}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      {/* Selection Checkbox */}
                      <div
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          selectedIds.has(media.id)
                            ? 'bg-navy text-white'
                            : 'bg-card/80 border border-line-2 dark:border-border-dark'
                        }`}
                      >
                        {selectedIds.has(media.id) && (
                          <Check className="w-4 h-4" />
                        )}
                      </div>

                      {/* Original source badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="green" size="sm">
                          <Upload className="w-3 h-3 mr-1" />
                          Original
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generated Media Grid */}
            {generatedMedia.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-ink-2">
                    Select Media ({selectedIds.size} selected)
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="text-xs text-work hover:text-work"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={selectNone}
                      className="text-xs text-work hover:text-work"
                    >
                      Select None
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {generatedMedia.map((media, index) => (
                    <div
                      key={media.asset_id || `media-${index}`}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedIds.has(media.asset_id || '')
                          ? 'border-work-dot ring-2 ring-blue-200'
                          : 'border-line dark:border-border-dark hover:border-line-2'
                      }`}
                      onClick={() =>
                        media.asset_id && toggleMediaSelection(media.asset_id)
                      }
                    >
                      {/* Media Preview */}
                      <div className="aspect-square bg-inset dark:bg-card-darkAlt">
                        {media.media_type === 'video' ? (
                          <video
                            src={media.url}
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt={media.filename}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      {/* Selection Checkbox */}
                      <div
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          selectedIds.has(media.asset_id || '')
                            ? 'bg-navy text-white'
                            : 'bg-card/80 border border-line-2 dark:border-border-dark'
                        }`}
                      >
                        {selectedIds.has(media.asset_id || '') && (
                          <Check className="w-4 h-4" />
                        )}
                      </div>

                      {/* AI source badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge
                          variant={
                            media.media_type === 'video' ? 'purple' : 'blue'
                          }
                          size="sm"
                        >
                          {media.media_type === 'video' ? (
                            <><Video className="w-3 h-3 mr-1" />AI Video</>
                          ) : (
                            <><Sparkles className="w-3 h-3 mr-1" />AI Image</>
                          )}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {allJobsComplete && generatedMedia.length === 0 && originalMedia.length === 0 && !loading && (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 mx-auto text-ink-3 mb-4" />
                <p className="text-ink-2">
                  No media was generated. Try regenerating with different
                  prompts.
                </p>
              </div>
            )}

            {/* Regeneration Info */}
            {allJobsComplete && capabilities && !regenerationUnlimited && (
              <div className="bg-ground dark:bg-card-darkAlt rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-2">
                    Regeneration attempts remaining
                  </span>
                  <span className="font-medium">
                    {regenerationsRemaining} /{' '}
                    {capabilities.regeneration_attempts_per_campaign}
                  </span>
                </div>
              </div>
            )}

            {/* Credit Cost Information */}
            {allJobsComplete && generatedMedia.length > 0 && subscription?.credits && (
              <div className="bg-work-soft border border-work/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Coins className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-3">
                    {/* Current Generation Cost */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">
                          Current Generation
                        </span>
                        <span className="text-sm font-semibold text-primary">
                          {calculateMediaCreditCost(generatedMedia)} credits used
                        </span>
                      </div>
                      <div className="text-xs text-primary">
                        {generatedMedia.filter(m => m.media_type === 'image').length} images ({imageCost} credits each) + {' '}
                        {generatedMedia.filter(m => m.media_type === 'video').length} videos ({videoCost} credits each)
                      </div>
                    </div>

                    {/* Regeneration Cost Warning */}
                    {regenerationsRemaining > 0 && (
                      <div className="pt-3 border-t border-work/30">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-blue-900">
                            Regeneration Cost
                          </span>
                          <span className="text-sm font-semibold text-primary">
                            {getRegenerationCreditCost()} credits
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary">
                            Available credits:
                          </span>
                          <span className={`font-medium ${
                            hasInsufficientCredits(getRegenerationCreditCost())
                              ? 'text-bad'
                              : 'text-ok'
                          }`}>
                            {subscription.credits.available_credits.toLocaleString()}
                          </span>
                        </div>

                        {/* Insufficient Credits Warning */}
                        {hasInsufficientCredits(getRegenerationCreditCost()) && (
                          <Alert variant="error" className="mt-3">
                            <div className="text-sm">
                              Insufficient credits for regeneration. You need {getRegenerationCreditCost()} credits but only have {subscription.credits.available_credits}.
                              <button
                                onClick={() => window.location.href = '/purchase-credits'}
                                className="ml-2 underline font-medium hover:no-underline"
                              >
                                Purchase credits
                              </button>
                            </div>
                          </Alert>
                        )}
                      </div>
                    )}

                    {/* Credit Balance Summary */}
                    <div className="pt-3 border-t border-work/30">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-primary">Monthly Credits</div>
                          <div className="font-semibold text-blue-900">
                            {subscription.credits.remaining_credits.toLocaleString()} / {subscription.credits.total_credits.toLocaleString()}
                          </div>
                        </div>
                        {subscription.credits.bonus_credits > 0 && (
                          <div>
                            <div className="text-primary">Bonus Credits</div>
                            <div className="font-semibold text-purple-600">
                              {subscription.credits.remaining_bonus_credits.toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </SidePanel.Body>

      <SidePanel.Footer align="between">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {/* Regenerate button hidden - Feature deferred pending user feedback
              {allJobsComplete && regenerationsRemaining > 0 && (
                <Button
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={
                    isRegenerating ||
                    isSubmitting ||
                    hasInsufficientCredits(getRegenerationCreditCost())
                  }
                  icon={<RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />}
                >
                  Regenerate
                  {subscription?.credits && hasInsufficientCredits(getRegenerationCreditCost()) && (
                    <span className="ml-1 text-xs text-bad">
                      (Insufficient credits)
                    </span>
                  )}
                </Button>
              )}
          */}
        </div>

        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={
            !allJobsComplete ||
            selectedIds.size === 0 ||
            isSubmitting ||
            isRegenerating
          }
          iconAfter={<ArrowRight className="w-4 h-4" />}
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner size="sm" />
              <span className="ml-2">Processing...</span>
            </>
          ) : (
            `Continue with ${selectedIds.size} selected`
          )}
        </Button>
      </SidePanel.Footer>
    </SidePanel>
  );
};

export default MediaGenerationCanvas;
